/**
 * Buffered snapshot interpolation (FIX A of the movement-feel pass).
 *
 * Server-authoritative movement (DetourCrowd) publishes each unit's position at
 * 30 Hz. Rendering that data by chasing the *latest* authoritative position with
 * a fixed per-frame lerp (the crude SPIKE approach) looks stepped: the target
 * jumps 30×/s while the client paints 60–144×/s, so every snapshot boundary is a
 * visible velocity discontinuity.
 *
 * The Source-engine `cl_interp` model fixes this: buffer recent authoritative
 * positions with their client arrival time, then render each unit a small FIXED
 * delay behind "now" and lerp between the two buffered snapshots that BRACKET the
 * render timestamp. With the render clock sitting ~2–3 ticks in the past there is
 * (almost) always a future snapshot to interpolate toward, so 30 Hz data becomes
 * smooth per-frame motion at any client framerate.
 *
 * Arrival behaviour (feeds FIX B): when a unit stops, the server stops emitting
 * new positions, so `record()` adds nothing and the buffer's newest snapshot
 * freezes. Once the render clock advances past that snapshot `sample()` holds at
 * it exactly, so per-frame rendered displacement decays smoothly to ZERO — the
 * run→idle test downstream then flips promptly and cleanly.
 *
 * Client-only; nothing here touches the server or the crowd.
 */

interface Snap {
  t: number; // client arrival time (rAF timestamp, ms)
  x: number;
  z: number;
}

// Discard buffered snapshots older than this behind the render clock. Must
// comfortably exceed the render delay so the bracketing pair is never pruned;
// a little slack also absorbs late packets. Cheap — a unit accrues at most a
// handful of entries in this window.
const MAX_AGE_MS = 1000;
// A recording gap longer than this (~3 patches at 30 Hz) means the unit was
// FROZEN (idle/arrived), not mid-stream — the next snapshot starts a new burst.
const FREEZE_GAP_MS = 100;
// Where the bridge snapshot is stamped relative to the burst's first snapshot:
// one patch back, so the first interpolation segment has a normal-cadence span.
const BRIDGE_BACKDATE_MS = 34;
// Bridge only organic move-starts. A first-snapshot jump beyond this is a
// teleport/respawn — snap to it instead (a bridged lerp would streak the mesh
// across the map in ~2 frames and set off the dash-trail VFX detector).
const BRIDGE_MAX_DIST = 2.0;
// De-jitter (D4, 2026-07-10): the server emits patches on a fixed 30Hz grid,
// but TCP delivery jitter (measured p95 ~42ms vs nominal 33ms on prod) warps
// the ARRIVAL spacing — and raw arrival-time stamping passes that warp 1:1
// into rendered velocity (a 20ms-late patch = one slow-motion segment + one
// fast-forward segment, ±20% speed ripple at patch frequency). Mid-burst
// snapshots are therefore RETIMED onto the nominal grid: stamped prev.t +
// NOMINAL, clamped to ±RETIME_SLACK_MS of the true arrival so the timeline
// stays anchored to the real clock (no unbounded drift — the interp delay
// keeps its calibration; jitter beyond the slack passes through partially
// rather than accumulating).
const NOMINAL_SPAN_MS = 1000 / 30;
const RETIME_SLACK_MS = 15;

export class SnapshotBuffer {
  private buf: Snap[] = [];
  private lastX = NaN;
  private lastZ = NaN;

  /**
   * Record an authoritative position. Deduped: only positions that actually
   * changed since the last record push a snapshot, so an idle unit (server
   * re-sending the same x/z, or the client reading an unchanged field every
   * frame) does NOT accumulate spurious entries — the buffer correctly freezes
   * on arrival. `now` is the current frame's rAF timestamp (ms).
   */
  record(x: number, z: number, now: number): void {
    if (x === this.lastX && z === this.lastZ) return;
    this.lastX = x;
    this.lastZ = z;
    // Move-start bridge (playtest 2026-07-09 "double take"): while idle nothing
    // records, so the newest snapshot is the arrival position from long ago. The
    // first moving snapshot would otherwise form a bracket spanning the whole
    // idle gap — the render clock lands at lerp-factor ≈ 1, JUMPS a patch-step
    // in one frame, then HOLDS ~delay ms until it crosses into fresh data
    // (run→idle→run anim flicker). Re-stamp the frozen position one patch before
    // the new snapshot so a move-start is a clean delayed ease. Teleport-scale
    // jumps skip the bridge and clear history: snap, don't swoosh.
    const newest = this.buf[this.buf.length - 1];
    let t = now;
    if (newest !== undefined && now - newest.t > FREEZE_GAP_MS) {
      if (Math.hypot(x - newest.x, z - newest.z) <= BRIDGE_MAX_DIST) {
        this.buf.push({ t: now - BRIDGE_BACKDATE_MS, x: newest.x, z: newest.z });
      } else {
        this.buf.length = 0;
      }
      // A burst-start anchors the smoothed timeline at the true arrival.
    } else if (newest !== undefined) {
      // Mid-burst: retime onto the nominal grid, slack-clamped to arrival (see
      // the D4 comment above). Strictly monotonic even for bunched same-ms
      // packets (the clamp's upper bound can otherwise land behind prev.t).
      const ideal = newest.t + NOMINAL_SPAN_MS;
      t = Math.min(Math.max(ideal, now - RETIME_SLACK_MS), now + RETIME_SLACK_MS);
      if (t <= newest.t) t = newest.t + 1;
    }
    this.buf.push({ t, x, z });
    const cutoff = now - MAX_AGE_MS;
    // Keep at least the two entries any interpolation needs.
    while (this.buf.length > 2 && (this.buf[0]?.t ?? Infinity) < cutoff) this.buf.shift();
  }

  /**
   * Position at `renderT` (= now − interpDelay). Lerps between the two snapshots
   * bracketing renderT. Clamps to the oldest snapshot if renderT precedes the
   * buffer (just-spawned), and HOLDS at the newest if renderT is past it (ran
   * out of future data — arrival, stall, or a dropped packet). Returns null only
   * while the buffer is empty, so the caller can fall back to the raw position.
   */
  sample(renderT: number): { x: number; z: number } | null {
    const b = this.buf;
    const first = b[0];
    if (first === undefined) return null;
    if (b.length === 1 || renderT <= first.t) return { x: first.x, z: first.z };
    for (let i = 0; i < b.length - 1; i++) {
      const a = b[i];
      const c = b[i + 1];
      if (a === undefined || c === undefined) break;
      if (renderT >= a.t && renderT <= c.t) {
        const span = c.t - a.t;
        const f = span > 0 ? (renderT - a.t) / span : 1;
        return { x: a.x + (c.x - a.x) * f, z: a.z + (c.z - a.z) * f };
      }
    }
    const last = b[b.length - 1];
    return last === undefined ? { x: first.x, z: first.z } : { x: last.x, z: last.z };
  }
}
